import posthog from 'posthog-js';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { getRequestSessionId } from '../api/requestSession';

type ErrorUser = {
  id?: string;
  email?: string;
  name?: string;
  accountName?: string;
  [key: string]: any;
};

type CaptureErrorOptions = {
  context?: string;
  metadata?: Record<string, any>;
  user?: ErrorUser;
  emitReplayTriggerEvent?: boolean;
};

type CaptureV4HandledErrorOptions = CaptureErrorOptions & {
  ignoredStatuses?: number[];
  ignoredMessages?: string[];
};

const REDACTED = '[REDACTED]';
export const POSTHOG_APP_NAME = 'Partna widget';
const SENSITIVE_KEYS = [
  'authorization',
  'password',
  'secret',
  'token',
  'otp',
  'pin',
];
const ALLOWED_USER_INFO_KEYS = ['email', 'senderemail', 'accountname'];
const PRIVATE_USER_INFO_EXACT_KEYS = ['name'];
const PRIVATE_USER_INFO_KEYS = [
  'fullname',
  'firstname',
  'lastname',
  'username',
  'registrationid',
  'phone',
  'address',
  'bvn',
  'accountnumber',
  'accountno',
  'bankaccount',
];

let errorMetadata: Record<string, any> = {};
let errorUser: ErrorUser = {};
let globalHandlersInitialized = false;
let legacyBugsnagInitialized = false;

export const isCypressRuntime = () =>
  typeof window !== 'undefined' && Boolean((window as any).Cypress);

const isSensitiveKey = (key: string) => {
  const normalizedKey = key.toLowerCase();
  return SENSITIVE_KEYS.some((sensitiveKey) =>
    normalizedKey.includes(sensitiveKey)
  );
};

const isAllowedUserInfoKey = (key: string) =>
  ALLOWED_USER_INFO_KEYS.includes(key.toLowerCase());

const isPrivateUserInfoKey = (key: string) => {
  const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');

  if (isAllowedUserInfoKey(normalizedKey)) return false;
  if (PRIVATE_USER_INFO_EXACT_KEYS.includes(normalizedKey)) return true;

  return PRIVATE_USER_INFO_KEYS.some((privateKey) =>
    normalizedKey.includes(privateKey)
  );
};

export const getPublicErrorUser = (user: ErrorUser = {}) => {
  const email = user.email || '';
  const accountName =
    user.accountName ||
    user.account_name ||
    user.account ||
    user.username ||
    '';

  return sanitizeErrorMetadata({
    ...(email ? { email } : {}),
    ...(accountName ? { accountName } : {}),
  });
};

export const sanitizeErrorSearch = (search = window.location.search) => {
  if (!search) return '';

  const params = new URLSearchParams(search);
  const sanitizedParams = new URLSearchParams();

  params.forEach((value, key) => {
    sanitizedParams.set(
      key,
      isSensitiveKey(key) || isPrivateUserInfoKey(key) ? REDACTED : value
    );
  });

  const sanitizedSearch = sanitizedParams.toString();
  return sanitizedSearch ? `?${sanitizedSearch}` : '';
};

export const sanitizeErrorUrl = (url?: string) => {
  if (!url) return url;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const sanitizedSearch = sanitizeErrorSearch(parsedUrl.search);
    const sanitizedPath = `${parsedUrl.pathname}${sanitizedSearch}${parsedUrl.hash}`;

    return url.startsWith('http')
      ? `${parsedUrl.origin}${sanitizedPath}`
      : sanitizedPath;
  } catch {
    const [path, search = ''] = url.split('?');
    return search ? `${path}${sanitizeErrorSearch(`?${search}`)}` : url;
  }
};

export const getErrorReferenceIds = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const freqId = getRequestSessionId();
  const rampReference =
    searchParams.get('rampReference') || searchParams.get('reference') || '';

  return sanitizeErrorMetadata({
    ...(freqId ? { freqId } : {}),
    ...(rampReference ? { rampReference } : {}),
  });
};

export const sanitizeErrorMetadata = (
  value: any,
  seen = new WeakSet<object>()
): any => {
  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeErrorMetadata(item, seen));
  }

  if (value && typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);

    return Object.keys(value).reduce<Record<string, any>>((metadata, key) => {
      metadata[key] =
        isSensitiveKey(key) || isPrivateUserInfoKey(key)
          ? REDACTED
          : sanitizeErrorMetadata(value[key], seen);

      return metadata;
    }, {});
  }

  if (typeof value === 'function') {
    return '[Function]';
  }

  return value;
};

const toError = (error: any) => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(error?.message || String(error || 'Unknown error'));
};

const getHandledErrorStatus = (error: any) =>
  error?.response?.status || error?.status || error?.statusCode;

const getHandledErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.response?.data?.title ||
  error?.message ||
  String(error || 'Unknown handled error');

const isIgnoredAxiosError = (error: any) => {
  if (!error?.isAxiosError) return false;

  const message = getHandledErrorMessage(error).toLowerCase();
  const code = String(error?.code || '').toUpperCase();

  return (
    message === 'network error' ||
    message.includes('request aborted') ||
    message.includes('request cancelled') ||
    message.includes('request canceled') ||
    code === 'ERR_NETWORK' ||
    code === 'ERR_CANCELED' ||
    code === 'ECONNABORTED' ||
    error?.name === 'CanceledError'
  );
};

const parseJsonLikeValue = (value: any) => {
  if (typeof value !== 'string') return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const pickHeaders = (headers: any, headerNames: string[]) => {
  if (!headers) return undefined;

  return headerNames.reduce<Record<string, any>>(
    (pickedHeaders, headerName) => {
      const headerValue =
        headers[headerName] || headers[headerName.toLowerCase()];

      if (headerValue) {
        pickedHeaders[headerName] = headerValue;
      }

      return pickedHeaders;
    },
    {}
  );
};

const getHandledErrorMetadata = (error: any) => ({
  status: getHandledErrorStatus(error),
  statusText: error?.response?.statusText,
  message: getHandledErrorMessage(error),
  code: error?.code || error?.response?.data?.code,
  errorName: error?.name,
  isAxiosError: error?.isAxiosError,
  method: error?.config?.method,
  url: sanitizeErrorUrl(error?.config?.url || error?.request?.responseURL),
  baseURL: sanitizeErrorUrl(error?.config?.baseURL),
  timeout: error?.config?.timeout,
  params: error?.config?.params,
  responseData: parseJsonLikeValue(error?.response?.data),
  responseHeaders: pickHeaders(error?.response?.headers, [
    'x-request-id',
    'x-correlation-id',
    'x-trace-id',
    'request-id',
  ]),
  requestHeaders: pickHeaders(error?.config?.headers, [
    'x-request-id',
    'x-correlation-id',
    'x-trace-id',
    'request-id',
  ]),
  request: {
    readyState: error?.request?.readyState,
    status: error?.request?.status,
    responseURL: sanitizeErrorUrl(error?.request?.responseURL),
  },
  axios: typeof error?.toJSON === 'function' ? error.toJSON() : undefined,
});

const getDefaultProperties = (context?: string) => ({
  app: POSTHOG_APP_NAME,
  app_name: POSTHOG_APP_NAME,
  context,
  environment: process.env.REACT_APP_ENVIRONMENT,
  mode: process.env.REACT_APP_MODE,
  path: window.location.pathname,
  search: sanitizeErrorSearch(),
  user: getPublicErrorUser(errorUser),
  ...getErrorReferenceIds(),
});

export const isV4Route = () => window.location.pathname.startsWith('/v4');

export const initializeLegacyBugsnag = () => {
  if (legacyBugsnagInitialized) return true;
  if (isCypressRuntime() || !process.env.REACT_APP_BUGSNAG_API_KEY) {
    return false;
  }

  legacyBugsnagInitialized = true;

  const searchParams = new URLSearchParams(window.location.search);
  const senderEmail =
    searchParams?.get('senderEmail') ||
    searchParams?.get('email') ||
    localStorage?.getItem('v4_email') ||
    localStorage?.getItem('email') ||
    '';

  Bugsnag.start({
    apiKey: `${process.env.REACT_APP_BUGSNAG_API_KEY}`,
    plugins: [new BugsnagPluginReact()],
    enabledReleaseStages: ['production', 'staging', 'alpha'],
    releaseStage: process.env.REACT_APP_ENVIRONMENT,
    user: {
      email: senderEmail,
    },
    onError: () => !isV4Route(),
  });

  return true;
};

export const emitExceptionReplayTriggerEvent = (context?: string) => {
  if (!isV4Route()) return false;

  try {
    posthog.capture('v4_exception_session_replay_triggered', {
      app: POSTHOG_APP_NAME,
      app_name: POSTHOG_APP_NAME,
      context,
      path: window.location.pathname,
      search: sanitizeErrorSearch(),
      ...getErrorReferenceIds(),
    });

    return true;
  } catch (error) {
    console.error(
      '[errorReporting] Failed to emit session replay trigger event:',
      error
    );
    return false;
  }
};

export const setErrorUser = (
  id?: string,
  email?: string,
  name?: string,
  metadata?: Record<string, any>
) => {
  const nextUser = sanitizeErrorMetadata({
    ...errorUser,
    ...metadata,
    id,
    email,
    name,
  });

  errorUser = getPublicErrorUser(nextUser);

  const distinctId = id || email || errorUser.accountName;
  if (!distinctId) return;

  if (!isV4Route()) {
    if (initializeLegacyBugsnag()) {
      Bugsnag.setUser(id, email, name);
    }
    return;
  }

  posthog.identify(distinctId, {
    ...getPublicErrorUser({
      ...metadata,
      email,
    }),
  });
};

export const addErrorMetadata = (section: string, metadata: any) => {
  if (!isV4Route()) {
    if (initializeLegacyBugsnag()) {
      Bugsnag.addMetadata(section, sanitizeErrorMetadata(metadata));
    }
    return;
  }

  errorMetadata = {
    ...errorMetadata,
    [section]: sanitizeErrorMetadata(metadata),
  };
};

export const clearErrorMetadata = (section?: string) => {
  if (!section) {
    errorMetadata = {};
    return;
  }

  const { [section]: _removed, ...remainingMetadata } = errorMetadata;
  errorMetadata = remainingMetadata;
};

const getErrorMetadataPropertyKey = (key: string) => {
  const normalizedKey = key
    .replace(/[^a-zA-Z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `error_metadata_${normalizedKey || 'value'}`;
};

export const getErrorMetadataProperties = (
  metadata: Record<string, any> = {}
) => {
  const combinedMetadata = sanitizeErrorMetadata({
    ...errorMetadata,
    ...metadata,
  });

  const flattenedMetadata = Object.entries(combinedMetadata).reduce<
    Record<string, any>
  >((properties, [key, value]) => {
    properties[getErrorMetadataPropertyKey(key)] = value;
    return properties;
  }, {});

  return {
    metadata: combinedMetadata,
    error_metadata: combinedMetadata,
    error_metadata_keys: Object.keys(combinedMetadata),
    ...flattenedMetadata,
  };
};

export const captureError = (error: any, options: CaptureErrorOptions = {}) => {
  if (options.user) {
    setErrorUser(
      options.user.id,
      options.user.email,
      options.user.name,
      options.user
    );
  }

  const shouldEmitReplayTriggerEvent =
    options.emitReplayTriggerEvent ?? isV4Route();
  const replayTriggerEventEmitted = shouldEmitReplayTriggerEvent
    ? emitExceptionReplayTriggerEvent(options.context)
    : false;

  const properties = sanitizeErrorMetadata({
    ...getDefaultProperties(options.context),
    replayTriggerEventEmitted,
    ...getErrorMetadataProperties(options.metadata),
  });

  const capturedError = toError(error);

  if (!isV4Route()) {
    if (initializeLegacyBugsnag()) {
      Bugsnag.notify(capturedError, (event) => {
        if (options.context) {
          event.context = options.context;
        }

        event.addMetadata('error reporting', properties);
      });
    }
    return;
  }

  const captureException = posthog.captureException?.bind(posthog);

  if (captureException) {
    captureException(capturedError, properties);
    return;
  }

  posthog.capture('application_error', {
    ...properties,
    error: sanitizeErrorMetadata(capturedError),
  });
};

export const captureV4HandledError = (
  error: any,
  options: CaptureV4HandledErrorOptions = {}
) => {
  if (!isV4Route()) return false;

  const status = getHandledErrorStatus(error);
  const message = getHandledErrorMessage(error);
  const ignoredStatuses = options.ignoredStatuses ?? [400, 401, 404, 422];
  const ignoredMessages = options.ignoredMessages ?? [];

  if (
    isIgnoredAxiosError(error) ||
    (status && ignoredStatuses.includes(status))
  ) {
    return false;
  }
  if (
    ignoredMessages.some((ignoredMessage) =>
      message.toLowerCase().includes(ignoredMessage.toLowerCase())
    )
  ) {
    return false;
  }

  captureError(error, {
    ...options,
    context: options.context || 'v4 handled error',
    metadata: {
      handledError: getHandledErrorMetadata(error),
      ...(options.metadata || {}),
    },
  });

  return true;
};

export const captureMessage = (
  message: string,
  options: CaptureErrorOptions = {}
) => {
  captureError(new Error(message), options);
};

export const initializeErrorReporting = () => {
  if (globalHandlersInitialized) return;

  globalHandlersInitialized = true;
  initializeLegacyBugsnag();

  const searchParams = new URLSearchParams(window.location.search);
  const email =
    searchParams?.get('senderEmail') ||
    searchParams?.get('email') ||
    localStorage?.getItem('v4_email') ||
    localStorage?.getItem('email') ||
    '';

  if (email) {
    setErrorUser(email, email);
  }

  window.addEventListener('error', (event) => {
    if (!isV4Route()) return;

    captureError(event.error || new Error(event.message), {
      context: 'window error',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!isV4Route()) return;

    captureError(event.reason, {
      context: 'unhandled promise rejection',
    });
  });
};
